import { Fragment } from 'react'

import type { GameConfig, GameManifest, ParamDef } from '@senses/games'


/**
 * Formulář poskládaný z manifestu hry. Trenér tak dostane ovládání
 * parametrů, aniž by pro novou hru bylo potřeba psát jediný řádek UI.
 */
export function ParamForm({
  manifest,
  value,
  onChange,
}: {
  manifest: GameManifest
  value: GameConfig
  onChange: (next: GameConfig) => void
}): React.JSX.Element {
  const set = (key: string, next: number | boolean | string): void => {
    onChange({ ...value, [key]: next })
  }

  const groups = groupParams(manifest.params)

  return (
    <div>
      {groups.map(({ name, basic, advanced }) => (
        <div className="param-group" key={name}>
          {name && <div className="param-group__title">{name}</div>}

          {basic.map((def) => (
            <ParamField key={def.key} def={def} value={value[def.key]} onChange={set} />
          ))}

          {advanced.length > 0 && (
            <details className="advanced">
              <summary>Pokročilé nastavení ({advanced.length})</summary>
              <div style={{ paddingTop: '0.75rem' }}>
                {advanced.map((def) => (
                  <ParamField key={def.key} def={def} value={value[def.key]} onChange={set} />
                ))}
              </div>
            </details>
          )}
        </div>
      ))}
    </div>
  )
}

function ParamField({
  def,
  value,
  onChange,
}: {
  def: ParamDef
  value: number | boolean | string | undefined
  onChange: (key: string, next: number | boolean | string) => void
}): React.JSX.Element {
  if (def.type === 'bool') {
    const checked = typeof value === 'boolean' ? value : def.default
    return (
      <div className="field">
        <div className="field--inline field">
          <input
            id={`p-${def.key}`}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(def.key, e.target.checked)}
          />
          <label htmlFor={`p-${def.key}`} className="field__label" style={{ cursor: 'pointer' }}>
            {def.label}
          </label>
        </div>
        {def.help && <span className="field__help">{def.help}</span>}
      </div>
    )
  }

  if (def.type === 'enum') {
    const current = typeof value === 'string' ? value : def.default
    return (
      <label className="field">
        <span className="field__label">{def.label}</span>
        <select value={current} onChange={(e) => onChange(def.key, e.target.value)}>
          {def.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {def.help && <span className="field__help">{def.help}</span>}
      </label>
    )
  }

  const current = typeof value === 'number' ? value : def.default
  const decimals = def.type === 'int' ? 0 : decimalsFor(def.step)

  /** Posuvník i pole se zapisují přes stejnou cestu, včetně ořezu na rozsah. */
  const commit = (raw: number): void => {
    if (!Number.isFinite(raw)) return
    const clamped = Math.min(def.max, Math.max(def.min, raw))
    onChange(def.key, def.type === 'int' ? Math.round(clamped) : roundTo(clamped, decimals))
  }

  return (
    <div className="field">
      <span className="field__label">{def.label}</span>
      <div className="param">
        <input
          type="range"
          min={def.min}
          max={def.max}
          step={def.step}
          value={current}
          onChange={(e) => commit(Number(e.target.value))}
          aria-label={def.label}
        />
        <div className="param__value">
          <input
            type="number"
            min={def.min}
            max={def.max}
            step={def.step}
            value={current}
            onChange={(e) => commit(Number(e.target.value))}
            aria-label={`${def.label} — hodnota`}
          />
          {def.unit && <span className="param__unit">{def.unit}</span>}
        </div>
      </div>
      {def.help && <span className="field__help">{def.help}</span>}
    </div>
  )
}

interface Group {
  name: string
  basic: ParamDef[]
  advanced: ParamDef[]
}

/** Rozdělí parametry na skupiny v pořadí, v jakém je manifest uvádí. */
function groupParams(params: readonly ParamDef[]): Group[] {
  const groups: Group[] = []

  for (const def of params) {
    const name = def.group ?? ''
    let group = groups.find((g) => g.name === name)
    if (!group) {
      group = { name, basic: [], advanced: [] }
      groups.push(group)
    }
    if (def.advanced) group.advanced.push(def)
    else group.basic.push(def)
  }

  return groups
}

function decimalsFor(step: number): number {
  const text = String(step)
  const dot = text.indexOf('.')
  return dot === -1 ? 0 : text.length - dot - 1
}

function roundTo(value: number, decimals: number): number {
  const k = 10 ** decimals
  return Math.round(value * k) / k
}

/**
 * Přehled nastavení pro čtení — používá se před spuštěním tréninku
 * a v detailu odehrané série.
 */
export function ParamSummary({
  manifest,
  params,
  keys,
}: {
  manifest: GameManifest
  params: Record<string, number | boolean | string>
  /** Které parametry ukázat; ve výchozím stavu prvních pět. */
  keys?: string[]
}): React.JSX.Element {
  const selected = keys
    ? manifest.params.filter((p) => keys.includes(p.key))
    : manifest.params.filter((p) => !p.advanced).slice(0, 5)

  return (
    <dl className="play__params">
      {selected.map((def) => (
        <Fragment key={def.key}>
          <div className="play__param">
            <dt>{def.label}</dt>
            <dd>{formatParam(def, params[def.key])}</dd>
          </div>
        </Fragment>
      ))}
    </dl>
  )
}

/**
 * Žebříček obtížnosti pro dané nastavení. Zobrazí se jen u her, které ho
 * v manifestu poskytují — ostatní hry se tvářit nemusí.
 */
export function LevelLadder({
  manifest,
  config,
  unit,
}: {
  manifest: GameManifest
  config: GameConfig
  unit?: string
}): React.JSX.Element | null {
  const rungs = manifest.levels?.(config)
  if (!rungs || rungs.length === 0) return null

  return (
    <div>
      <div className="stat__label" style={{ marginBottom: '0.5rem' }}>
        Žebříček levelů
      </div>
      <div className="ladder">
        {rungs.map((rung) => (
          <div className="ladder__rung" key={rung.level}>
            <span className="ladder__level">{rung.level}</span>
            <span className="ladder__value">
              {rung.value}
              {unit && <span className="dim"> {unit}</span>}
            </span>
          </div>
        ))}
      </div>
      <p className="field__help" style={{ marginTop: '0.5rem' }}>
        Úspěšný pokus posune vždy na další level. Chyba rychlost sníží, ale ne až
        na začátek levelu — další úspěch tedy vrátí na tentýž stupeň a teprve ten
        následující posune výš.
      </p>
    </div>
  )
}

function formatParam(def: ParamDef, value: number | boolean | string | undefined): string {
  if (def.type === 'bool') {
    const on = typeof value === 'boolean' ? value : def.default
    return on ? 'zapnuto' : 'vypnuto'
  }
  if (def.type === 'enum') {
    const current = typeof value === 'string' ? value : def.default
    return def.options.find((o) => o.value === current)?.label ?? current
  }
  const current = typeof value === 'number' ? value : def.default
  return `${current}${def.unit ? ` ${def.unit}` : ''}`
}
